import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import Shell from '../dashboard/Shell';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useToast } from '../../components/ui/Toast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import {
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  FileText,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  UserX,
  Trash2,
  Filter,
  List,
  Grid,
  CalendarDays
} from 'lucide-react';
import './MerchantAppointments.css';

const MerchantAppointments = () => {
  const { getAxios } = useSecureAuth();

  // Currency context for proper conversion
  const { formatInDisplayCurrency, accountCurrency } = useCurrency();
  const toast = useToast();
  const { confirm } = useConfirm();
  const store = useSelector(state => state.store?.current);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0 });

  // Calendar state
  const [calendarView, setCalendarView] = useState('month'); // 'week' or 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [viewMode, setViewMode] = useState('split'); // 'split', 'calendar', 'list'

  useEffect(() => {
    fetchAppointments();
  }, [filter, selectedDate, pagination.current]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const axios = getAxios();

      const params = {
        page: pagination.current,
        limit: 100 // Fetch more for calendar view
      };

      if (filter !== 'all') {
        params.status = filter.toUpperCase();
      }

      if (selectedDate) {
        params.date = selectedDate;
      }

      const response = await axios.get('/appointments', { params });

      setAppointments(response.data.appointments || []);
      setPagination(response.data.pagination || { current: 1, pages: 1, total: 0 });
      setError(null);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError(err.response?.data?.error || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (appointmentId, newStatus) => {
    try {
      const axios = getAxios();
      await axios.put(`/appointments/${appointmentId}/status`, {
        status: newStatus
      });
      fetchAppointments();
      setSelectedAppointment(null);
      toast.success('Appointment status updated successfully');
    } catch (err) {
      console.error('Error updating appointment status:', err);
      toast.error(err.response?.data?.error || 'Failed to update appointment status');
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    const confirmed = await confirm('Are you sure you want to delete this appointment?', {
      title: 'Delete Appointment',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    if (!confirmed) return;

    try {
      const axios = getAxios();
      await axios.delete(`/appointments/${appointmentId}`);
      fetchAppointments();
      setSelectedAppointment(null);
      toast.success('Appointment deleted successfully');
    } catch (err) {
      console.error('Error deleting appointment:', err);
      toast.error(err.response?.data?.error || 'Failed to delete appointment');
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Previous month days
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthDays - i),
        isCurrentMonth: false
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }

    // Next month days
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }

    return days;
  };

  const getWeekDays = (date) => {
    const days = [];
    const day = date.getDay();
    const diff = date.getDate() - day;

    for (let i = 0; i < 7; i++) {
      days.push({
        date: new Date(date.getFullYear(), date.getMonth(), diff + i),
        isCurrentMonth: true
      });
    }

    return days;
  };

  const calendarDays = useMemo(() => {
    return calendarView === 'month' ? getDaysInMonth(currentDate) : getWeekDays(currentDate);
  }, [currentDate, calendarView]);

  const appointmentsByDate = useMemo(() => {
    const map = {};
    appointments.forEach(apt => {
      const dateKey = apt.appointmentDate.split('T')[0];
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(apt);
    });
    return map;
  }, [appointments]);

  const navigateCalendar = (direction) => {
    const newDate = new Date(currentDate);
    if (calendarView === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else {
      newDate.setDate(newDate.getDate() + (direction * 7));
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate('');
  };

  // Helper to format date as YYYY-MM-DD in local timezone (avoids UTC conversion issues)
  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateClick = (date) => {
    const dateStr = formatDateKey(date);
    if (selectedDate === dateStr) {
      setSelectedDate('');
    } else {
      setSelectedDate(dateStr);
    }
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const formatDateDisplay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return selectedDate === formatDateKey(date);
  };

  const getStatusConfig = (status) => {
    const configs = {
      'CONFIRMED': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
      'PENDING': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
      'PENDING_PAYMENT': { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
      'CANCELLED': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
      'COMPLETED': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
      'NO_SHOW': { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', dot: 'bg-gray-500' }
    };
    return configs[status] || configs['PENDING'];
  };

  const getStatusLabel = (status) => status.replace('_', ' ');

  const filteredAppointments = useMemo(() => {
    let filtered = [...appointments];

    // Sort by date (most recent first), then by time
    filtered.sort((a, b) => {
      const dateA = new Date(a.appointmentDate + 'T' + a.appointmentTime);
      const dateB = new Date(b.appointmentDate + 'T' + b.appointmentTime);
      return dateA - dateB;
    });

    return filtered;
  }, [appointments]);

  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const todayAppts = appointments.filter(a => a.appointmentDate.split('T')[0] === today);
    const confirmed = appointments.filter(a => a.status === 'CONFIRMED').length;
    const pending = appointments.filter(a => a.status === 'PENDING' || a.status === 'PENDING_PAYMENT').length;

    return {
      total: appointments.length,
      today: todayAppts.length,
      confirmed,
      pending
    };
  }, [appointments]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading && appointments.length === 0) {
    return (
      <Shell>
        <div className="ma-container">
          <div className="ma-loading">
            <div className="ma-loading-spinner"></div>
            <p>Loading appointments...</p>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="ma-container">
        {/* Header */}
        <div className="ma-header">
          <div className="ma-header-left">
            <h1>Appointments</h1>
            <p>Manage your bookings and schedule</p>
          </div>
          <div className="ma-header-right">
            <div className="ma-view-toggle">
              <button
                className={`ma-view-btn ${viewMode === 'split' ? 'active' : ''}`}
                onClick={() => setViewMode('split')}
                title="Split View"
              >
                <Grid size={18} />
              </button>
              <button
                className={`ma-view-btn ${viewMode === 'calendar' ? 'active' : ''}`}
                onClick={() => setViewMode('calendar')}
                title="Calendar View"
              >
                <CalendarDays size={18} />
              </button>
              <button
                className={`ma-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="ma-stats">
          <div className="ma-stat-card">
            <span className="ma-stat-value">{stats.total}</span>
            <span className="ma-stat-label">Total</span>
          </div>
          <div className="ma-stat-card today">
            <span className="ma-stat-value">{stats.today}</span>
            <span className="ma-stat-label">Today</span>
          </div>
          <div className="ma-stat-card confirmed">
            <span className="ma-stat-value">{stats.confirmed}</span>
            <span className="ma-stat-label">Confirmed</span>
          </div>
          <div className="ma-stat-card pending">
            <span className="ma-stat-value">{stats.pending}</span>
            <span className="ma-stat-label">Pending</span>
          </div>
        </div>

        {error && (
          <div className="ma-error">
            <p>{error}</p>
          </div>
        )}

        <div className={`ma-content ${viewMode}`}>
          {/* Calendar Section */}
          {(viewMode === 'split' || viewMode === 'calendar') && (
            <div className="ma-calendar-section">
              <div className="ma-calendar-card">
                {/* Calendar Header */}
                <div className="ma-calendar-header">
                  <div className="ma-calendar-nav">
                    <button onClick={() => navigateCalendar(-1)} className="ma-nav-btn">
                      <ChevronLeft size={20} />
                    </button>
                    <h3>
                      {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                    </h3>
                    <button onClick={() => navigateCalendar(1)} className="ma-nav-btn">
                      <ChevronRight size={20} />
                    </button>
                  </div>
                  <div className="ma-calendar-actions">
                    <button onClick={goToToday} className="ma-today-btn">
                      Today
                    </button>
                    <div className="ma-calendar-view-toggle">
                      <button
                        className={`ma-cal-view-btn ${calendarView === 'week' ? 'active' : ''}`}
                        onClick={() => setCalendarView('week')}
                      >
                        Week
                      </button>
                      <button
                        className={`ma-cal-view-btn ${calendarView === 'month' ? 'active' : ''}`}
                        onClick={() => setCalendarView('month')}
                      >
                        Month
                      </button>
                    </div>
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className="ma-calendar-grid">
                  {/* Week day headers */}
                  <div className="ma-calendar-weekdays">
                    {weekDays.map(day => (
                      <div key={day} className="ma-weekday">{day}</div>
                    ))}
                  </div>

                  {/* Days */}
                  <div className={`ma-calendar-days ${calendarView}`}>
                    {calendarDays.map((day, index) => {
                      const dateKey = formatDateKey(day.date);
                      const dayAppointments = appointmentsByDate[dateKey] || [];
                      const hasAppointments = dayAppointments.length > 0;

                      return (
                        <div
                          key={index}
                          className={`ma-calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${isToday(day.date) ? 'today' : ''} ${isSelected(day.date) ? 'selected' : ''} ${hasAppointments ? 'has-appointments' : ''}`}
                          onClick={() => handleDateClick(day.date)}
                        >
                          <span className="ma-day-number">{day.date.getDate()}</span>
                          {hasAppointments && (
                            <div className="ma-day-appointments">
                              {dayAppointments.slice(0, 3).map((apt, i) => {
                                const config = getStatusConfig(apt.status);
                                return (
                                  <div
                                    key={i}
                                    className={`ma-day-appointment ${config.bg} ${config.text}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedAppointment(apt);
                                    }}
                                  >
                                    <span className={`ma-apt-dot ${config.dot}`}></span>
                                    <span className="ma-apt-time">{apt.appointmentTime?.slice(0, 5)}</span>
                                    <span className="ma-apt-name">{apt.serviceName?.slice(0, 10)}</span>
                                  </div>
                                );
                              })}
                              {dayAppointments.length > 3 && (
                                <div className="ma-more-appointments">
                                  +{dayAppointments.length - 3} more
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Appointments List Section */}
          {(viewMode === 'split' || viewMode === 'list') && (
            <div className="ma-list-section">
              {/* Filters */}
              <div className="ma-filters">
                <div className="ma-filter-left">
                  <Filter size={16} />
                  <select
                    value={filter}
                    onChange={(e) => {
                      setFilter(e.target.value);
                      setPagination(prev => ({ ...prev, current: 1 }));
                    }}
                    className="ma-filter-select"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="pending_payment">Pending Payment</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="no_show">No Show</option>
                  </select>
                </div>
                {selectedDate && (
                  <button className="ma-clear-date" onClick={() => setSelectedDate('')}>
                    <X size={14} />
                    Clear {formatDateDisplay(selectedDate)}
                  </button>
                )}
              </div>

              {/* Appointments */}
              {filteredAppointments.length === 0 ? (
                <div className="ma-empty">
                  <Calendar size={48} strokeWidth={1} />
                  <h3>No appointments found</h3>
                  <p>
                    {selectedDate
                      ? `No appointments on ${formatFullDate(selectedDate)}`
                      : 'Customers can book appointments through your storefront'}
                  </p>
                </div>
              ) : (
                <div className="ma-appointments-list">
                  {filteredAppointments.map((appointment) => {
                    const statusConfig = getStatusConfig(appointment.status);
                    return (
                      <div
                        key={appointment.id}
                        className={`ma-appointment-card ${selectedAppointment?.id === appointment.id ? 'selected' : ''}`}
                        onClick={() => setSelectedAppointment(appointment)}
                      >
                        <div className="ma-apt-header">
                          <div className="ma-apt-info">
                            <h4>{appointment.serviceName}</h4>
                            <span className={`ma-apt-status ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border}`}>
                              {getStatusLabel(appointment.status)}
                            </span>
                          </div>
                          <span className="ma-apt-price">
                            {formatInDisplayCurrency(appointment.servicePrice, store?.currency || accountCurrency)}
                          </span>
                        </div>

                        <div className="ma-apt-details">
                          <div className="ma-apt-detail">
                            <Calendar size={14} />
                            <span>{formatDateDisplay(appointment.appointmentDate)}</span>
                          </div>
                          <div className="ma-apt-detail">
                            <Clock size={14} />
                            <span>{appointment.appointmentTime} ({appointment.serviceDuration} min)</span>
                          </div>
                          <div className="ma-apt-detail">
                            <User size={14} />
                            <span>{appointment.customerName}</span>
                          </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="ma-apt-actions">
                          {appointment.status === 'PENDING' && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleStatusUpdate(appointment.id, 'CONFIRMED'); }}
                                className="ma-action-btn approve"
                                title="Approve"
                              >
                                <Check size={16} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleStatusUpdate(appointment.id, 'CANCELLED'); }}
                                className="ma-action-btn decline"
                                title="Decline"
                              >
                                <X size={16} />
                              </button>
                            </>
                          )}
                          {appointment.status === 'CONFIRMED' && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleStatusUpdate(appointment.id, 'COMPLETED'); }}
                                className="ma-action-btn complete"
                                title="Mark Complete"
                              >
                                <Check size={16} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleStatusUpdate(appointment.id, 'NO_SHOW'); }}
                                className="ma-action-btn no-show"
                                title="No Show"
                              >
                                <UserX size={16} />
                              </button>
                            </>
                          )}
                          {(appointment.status === 'CANCELLED' || appointment.status === 'COMPLETED') && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteAppointment(appointment.id); }}
                              className="ma-action-btn delete"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="ma-pagination">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
                    disabled={pagination.current === 1}
                    className="ma-page-btn"
                  >
                    <ChevronLeft size={16} />
                    Prev
                  </button>
                  <span className="ma-page-info">
                    {pagination.current} / {pagination.pages}
                  </span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
                    disabled={pagination.current === pagination.pages}
                    className="ma-page-btn"
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Appointment Detail Modal/Sidebar */}
        {selectedAppointment && (
          <div className="ma-detail-overlay" onClick={() => setSelectedAppointment(null)}>
            <div className="ma-detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ma-detail-header">
                <h3>Appointment Details</h3>
                <button className="ma-close-btn" onClick={() => setSelectedAppointment(null)}>
                  <X size={20} />
                </button>
              </div>

              <div className="ma-detail-content">
                <div className="ma-detail-service">
                  <h4>{selectedAppointment.serviceName}</h4>
                  <span className={`ma-apt-status ${getStatusConfig(selectedAppointment.status).bg} ${getStatusConfig(selectedAppointment.status).text}`}>
                    {getStatusLabel(selectedAppointment.status)}
                  </span>
                </div>

                <div className="ma-detail-price">
                  {formatInDisplayCurrency(selectedAppointment.servicePrice, store?.currency || accountCurrency)}
                </div>

                <div className="ma-detail-rows">
                  <div className="ma-detail-row">
                    <Calendar size={16} />
                    <div>
                      <label>Date</label>
                      <span>{formatFullDate(selectedAppointment.appointmentDate)}</span>
                    </div>
                  </div>

                  <div className="ma-detail-row">
                    <Clock size={16} />
                    <div>
                      <label>Time</label>
                      <span>{selectedAppointment.appointmentTime} ({selectedAppointment.serviceDuration} minutes)</span>
                    </div>
                  </div>

                  <div className="ma-detail-row">
                    <User size={16} />
                    <div>
                      <label>Customer</label>
                      <span>{selectedAppointment.customerName}</span>
                    </div>
                  </div>

                  <div className="ma-detail-row">
                    <Mail size={16} />
                    <div>
                      <label>Email</label>
                      <span>{selectedAppointment.customerEmail}</span>
                    </div>
                  </div>

                  {selectedAppointment.customerPhone && (
                    <div className="ma-detail-row">
                      <Phone size={16} />
                      <div>
                        <label>Phone</label>
                        <span>{selectedAppointment.customerPhone}</span>
                      </div>
                    </div>
                  )}

                  {selectedAppointment.notes && (
                    <div className="ma-detail-row">
                      <FileText size={16} />
                      <div>
                        <label>Notes</label>
                        <span>{selectedAppointment.notes}</span>
                      </div>
                    </div>
                  )}

                  {selectedAppointment.paymentStatus && (
                    <div className="ma-detail-row">
                      <CreditCard size={16} />
                      <div>
                        <label>Payment</label>
                        <span>{selectedAppointment.paymentStatus}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="ma-detail-actions">
                  {selectedAppointment.status === 'PENDING' && (
                    <>
                      <button
                        onClick={() => handleStatusUpdate(selectedAppointment.id, 'CONFIRMED')}
                        className="ma-modal-btn approve"
                      >
                        <Check size={18} />
                        Approve Appointment
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(selectedAppointment.id, 'CANCELLED')}
                        className="ma-modal-btn decline"
                      >
                        <X size={18} />
                        Decline
                      </button>
                    </>
                  )}

                  {selectedAppointment.status === 'CONFIRMED' && (
                    <>
                      <button
                        onClick={() => handleStatusUpdate(selectedAppointment.id, 'COMPLETED')}
                        className="ma-modal-btn complete"
                      >
                        <Check size={18} />
                        Mark as Completed
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(selectedAppointment.id, 'NO_SHOW')}
                        className="ma-modal-btn no-show"
                      >
                        <UserX size={18} />
                        No Show
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(selectedAppointment.id, 'CANCELLED')}
                        className="ma-modal-btn cancel"
                      >
                        <X size={18} />
                        Cancel
                      </button>
                    </>
                  )}

                  {(selectedAppointment.status === 'CANCELLED' || selectedAppointment.status === 'COMPLETED') && (
                    <button
                      onClick={() => handleDeleteAppointment(selectedAppointment.id)}
                      className="ma-modal-btn delete"
                    >
                      <Trash2 size={18} />
                      Delete Appointment
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
};

export default MerchantAppointments;
